/**
 * leadDirectory.ts — the PURE half of the Control Center "My Customers" list.
 *
 * ⚠️ WHY THIS EXISTS (CPA Jake, 2026-10-09): "under my customers, it says and shows
 * only 100 customers." He had 1,585. The page read `leads` from `useClientData`,
 * whose subscription was `orderBy('lastInteractionAt','desc'), limit(100)`, and then
 * presented `leads.length` as his total — "All (100)", "Showing 20 of 100". Search
 * filtered those 100 too, and clicking any contact past the newest 100 bounced him
 * back to the list, because the detail page looked the contact up in the same
 * capped array. One cap, four lies.
 *
 * THE RULE THIS MODULE ENCODES: a number shown to a client as a TOTAL comes from a
 * server-side count (Firestore count() aggregation), never from the length of a
 * page we happened to load. The rows themselves are loaded in full, in chunks, so
 * search and filters reach every contact — and once loading completes, the loaded
 * set and the server count describe the same population.
 */
import { tsToMs } from '../timestamps'
import { customerStandingOf, type CustomerStanding } from './customerStanding'
import { isOutsidePerson, isTestContactName, type LeadExclusionClient } from '../leadAttribution'

/** Status pills on the page, in display order ('All' is added by the page). */
export const DIRECTORY_STATUSES = ['new', 'qualified', 'booked', 'nurture', 'closed'] as const

/** Rows fetched per round trip. ~1 KB per lead doc → ~500 KB per chunk. */
export const DIRECTORY_CHUNK_SIZE = 500

/**
 * The slim row the list needs. `interactions` (the bulk of a lead doc — 1.5 MB of
 * Jake's 1,585 docs) is deliberately dropped so 1,500+ rows stay light in memory.
 */
export interface DirectoryRow {
  id: string
  name?: string
  phone?: string
  email?: string
  source: string
  status: string
  /** The lead's pipeline stage id (leads/pipeline on the backend); absent for customers / unstaged. */
  stageId?: string
  /** A paying customer — outside the sales pipeline (current OR past). */
  isCustomer: boolean
  /** lead / customer / past-customer (leads/customerStanding.ts — the owner's word, else the last service date). */
  standing: CustomerStanding
  createdAt: unknown
  lastInteractionAt: unknown
  /** Set on someone selling to the business (backend leads/solicitation.ts) — never counted. */
  notACustomer?: { reason: string }
}

function str(v: unknown): string | undefined {
  return typeof v === 'string' && v.length > 0 ? v : undefined
}

/** PURE — project a lead document onto the row the list renders. */
export function toDirectoryRow(id: string, data: Record<string, unknown>, nowMs: number = Date.now(), afterDays?: number): DirectoryRow {
  return {
    id,
    name: str(data.name),
    phone: str(data.phone) ?? str(data.phoneNumber),
    email: str(data.email),
    source: str(data.source) ?? 'unknown',
    status: str(data.status) ?? 'new',
    stageId: str((data.pipeline as { stageId?: unknown } | undefined)?.stageId),
    isCustomer: data.relationship === 'customer',
    standing: customerStandingOf(data, nowMs, afterDays),
    createdAt: data.createdAt ?? null,
    lastInteractionAt: data.lastInteractionAt ?? null,
    ...(str((data.notACustomer as { reason?: unknown } | undefined)?.reason)
      ? { notACustomer: { reason: (data.notACustomer as { reason: string }).reason } } : {}),
  }
}

/** PURE — newest activity first; rows with no timestamp sink to the bottom. */
export function sortDirectoryRows(rows: readonly DirectoryRow[]): DirectoryRow[] {
  const key = (r: DirectoryRow) => tsToMs(r.lastInteractionAt) ?? tsToMs(r.createdAt) ?? 0
  return [...rows].sort((a, b) => key(b) - key(a) || a.id.localeCompare(b.id))
}

/**
 * PURE — page through a collection until it is exhausted.
 *
 * `fetchPage(cursor)` returns one page and the cursor for the next. A page shorter
 * than `pageSize` ends the loop. `onPage` fires after every page so the list can
 * render progressively. `isCancelled` lets an unmounted component stop paying for
 * reads. The fetcher is injected so the loop is testable without Firestore.
 */
export async function loadAllPages<Row, Cursor>(
  fetchPage: (cursor: Cursor | null) => Promise<{ rows: Row[]; cursor: Cursor | null }>,
  pageSize: number,
  onPage: (soFar: Row[]) => void,
  isCancelled: () => boolean = () => false,
): Promise<{ rows: Row[]; complete: boolean }> {
  const all: Row[] = []
  let cursor: Cursor | null = null
  // Hard stop far above any real client — guards against a cursor that never advances.
  for (let i = 0; i < 1000; i += 1) {
    if (isCancelled()) return { rows: all, complete: false }
    const page = await fetchPage(cursor)
    if (isCancelled()) return { rows: all, complete: false }
    all.push(...page.rows)
    onPage(all)
    if (page.rows.length < pageSize || page.cursor === null) return { rows: all, complete: true }
    cursor = page.cursor
  }
  return { rows: all, complete: false }
}

export interface DirectoryFilter {
  status: string   // 'All' or a status value
  source: string   // 'All Sources' or a source value
  search: string
}

function digits(s: string): string {
  return s.replace(/\D+/g, '')
}

/**
 * PURE — does this row match the search box? Name / email by substring
 * (case-insensitive); phone by DIGITS, so "2085551234" finds "(208) 555-1234".
 */
export function matchesSearch(row: DirectoryRow, search: string): boolean {
  const q = search.trim().toLowerCase()
  if (!q) return true
  if ((row.name ?? '').toLowerCase().includes(q)) return true
  if ((row.email ?? '').toLowerCase().includes(q)) return true
  if ((row.phone ?? '').toLowerCase().includes(q)) return true
  const qd = digits(q)
  return qd.length >= 3 && digits(row.phone ?? '').includes(qd)
}

/** PURE — apply the page's status / source / search filters to every loaded row. */
export function filterDirectory(rows: readonly DirectoryRow[], f: DirectoryFilter): DirectoryRow[] {
  return rows.filter((r) => {
    if (f.status !== 'All' && r.status !== f.status) return false
    if (f.source !== 'All Sources' && r.source !== f.source) return false
    return matchesSearch(r, f.search)
  })
}

/** Server-side counts (Firestore count() aggregation). `null` = not (yet) known. */
export interface DirectoryServerCounts {
  total: number | null
  byStatus: Record<string, number | null>
}

/**
 * PURE — the numbers on the status pills.
 *
 * Each pill is the SERVER count for that status, minus the rows that are never
 * counted (the owner's own number, our lines — `notCountedIds`). Those rows stay
 * in the list; they just never inflate a total. When a server count is missing
 * (the aggregation failed) the pill falls back to the loaded rows ONLY if loading
 * is complete — a partial load is never presented as a total. Otherwise `null`,
 * which the page renders as "…".
 */
export function directoryPillCounts(input: {
  server: DirectoryServerCounts
  rows: readonly DirectoryRow[]
  notCountedIds: ReadonlySet<string>
  complete: boolean
}): Record<string, number | null> {
  const { server, rows, notCountedIds, complete } = input
  const excludedByStatus: Record<string, number> = {}
  let excludedTotal = 0
  const loadedByStatus: Record<string, number> = {}
  for (const r of rows) {
    loadedByStatus[r.status] = (loadedByStatus[r.status] ?? 0) + 1
    if (notCountedIds.has(r.id)) {
      excludedTotal += 1
      excludedByStatus[r.status] = (excludedByStatus[r.status] ?? 0) + 1
    }
  }
  const pick = (serverN: number | null | undefined, loadedN: number, excludedN: number): number | null => {
    if (typeof serverN === 'number') return Math.max(0, serverN - excludedN)
    return complete ? Math.max(0, loadedN - excludedN) : null
  }
  const out: Record<string, number | null> = {
    All: pick(server.total, rows.length, excludedTotal),
  }
  for (const s of DIRECTORY_STATUSES) {
    out[s] = pick(server.byStatus[s], loadedByStatus[s] ?? 0, excludedByStatus[s] ?? 0)
  }
  return out
}

/**
 * PURE — the "Showing X of Y" line. With no filter active the denominator is the
 * SERVER total (every row in the list, counted or not). With a filter active it is
 * the number of matches — exact once loading completes, labelled "so far" before.
 */
export function directoryFooter(input: {
  shown: number
  matched: number
  serverTotal: number | null
  loaded: number
  complete: boolean
  filtered: boolean
}): string {
  const { shown, matched, serverTotal, loaded, complete, filtered } = input
  const fmt = (n: number) => n.toLocaleString('en-US')
  if (filtered) {
    return complete
      ? `Showing ${fmt(shown)} of ${fmt(matched)} matching contacts`
      : `Showing ${fmt(shown)} of ${fmt(matched)} matches so far — still loading (${fmt(loaded)}${serverTotal !== null ? ` of ${fmt(serverTotal)}` : ''} contacts)`
  }
  const total = serverTotal ?? (complete ? loaded : null)
  if (total === null) return `Showing ${fmt(shown)} contacts — still loading`
  const loadingNote = complete ? '' : ` — loading (${fmt(loaded)} of ${fmt(total)})`
  return `Showing ${fmt(shown)} of ${fmt(total)} contacts${loadingNote}`
}

/**
 * PURE — the rows the Customers list shows, and which loaded rows are never COUNTED
 * (Control Center audit E8, 2026-10-28). A self-labelled test contact ("TEST Rudy cutover
 * check 2") is not listed; the owner, our own lines, tests and people selling to the business
 * are never counted — the same WHO rule (isOutsidePerson) the server's counts use. Here, not in
 * the page, so the list and its pills are one derivation.
 */
export function directoryView(
  rows: readonly DirectoryRow[],
  client: LeadExclusionClient | null | undefined,
): { listed: DirectoryRow[]; hiddenTestContacts: number; notCountedIds: Set<string> } {
  const listed = rows.filter((r) => !isTestContactName(r.name))
  const notCountedIds = new Set(rows.filter((r) => !isOutsidePerson(r as never, client)).map((r) => r.id))
  return { listed, hiddenTestContacts: rows.length - listed.length, notCountedIds }
}
